/**
 * Random Madras palette generation.
 *
 * Random Madras stays random for new artworks, but the randomness is derived from
 * the artwork seed so any palette can be reproduced later. The palette PRNG lives
 * in its own namespace, so palette draws never consume or shift the pattern RNG
 * stream for the same seed.
 */

/** Namespace for the palette PRNG so it stays independent of pattern generation. */
export const RANDOM_MADRAS_RNG_NAMESPACE = 'random-madras-palette';

/** Fallback seed string, matching the backend PRNG convention for empty seeds. */
const SEED_FALLBACK = 'madras';

/** Curated authentic Madras/South Indian color swatches. */
export const MADRAS_SWATCH = [
  '#cc0033', // Madras red
  '#ffee88', // pale yellow
  '#004477', // indigo blue
  '#ffffff', // white
  '#e63946', // deep pink-red
  '#f1faee', // off-white
  '#a8dadc', // pale blue
  '#457b9d', // blue
  '#ffd700', // gold
  '#b8860b', // dark gold
  '#8b0000', // maroon
  '#f7c873', // pastel yellow
  '#e94f37', // orange-red
  '#393e41', // charcoal
  '#3f88c5', // sky blue
  '#f7cac9', // pastel pink
  '#92a8d1', // pastel blue
  '#034f84', // navy
  '#f7786b', // coral
  '#1d3557', // deep blue
  '#d72631', // chili red
  '#a2d5c6', // mint
  '#077b8a', // teal
  '#5c3c92', // purple
  '#f4f4f4', // light gray
  '#1a2634', // dark indigo
  '#ffb347', // mango
  '#ff6961', // watermelon
  '#6a0572', // plum
  '#fff8e7', // cream
  '#b7a57a', // tan
  '#e5e5e5', // light gray
  '#ff8f1c', // orange
  '#000000', // black (for accent)
  '#a6192e', // Harvard crimson
  '#005a9c', // blue
  '#c8102e', // tartan red
  '#008272', // tartan green
  '#007a3d', // Stewart green
  '#ef4135', // fashion red
  '#0055a4', // French blue
  '#f6aa00', // Tokyo yellow
  '#7c4f35', // brown
  '#eac086', // skin tone
  '#d1a074', // skin tone
  '#a8755c', // skin tone
  '#4b2e1f', // skin tone
];

/** White / near-white options; a Random Madras palette always ends with one. */
export const MADRAS_WHITES = ['#ffffff', '#fff8e7', '#f1faee', '#f4f4f4'];

/** XMUR3 string hash — same family as the pattern generator PRNG. */
function xmur3(str: string): [number, number, number, number] {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ ch, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ ch, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ ch, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ ch, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

/** SFC32 — fast 32-bit PRNG, same family as the pattern generator PRNG. */
function sfc32(a: number, b: number, c: number, d: number): () => number {
  return function next() {
    a |= 0;
    b |= 0;
    c |= 0;
    d |= 0;
    const t = (((a + b) | 0) + c + d) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    return (t >>> 0) / 4294967296;
  };
}

/** Deterministic RNG for one seed inside one namespace. */
export function createSeededRandom(seed: string, namespace: string): () => number {
  const key = `${namespace}:${seed || SEED_FALLBACK}`;
  const [a, b, c, d] = xmur3(key);
  return sfc32(a, b, c, d);
}

/**
 * Random Madras palette for one artwork seed: 3-5 non-white swatch colors with
 * duplicate avoidance, then one white / near-white. Same seed, same palette.
 */
export function createRandomMadrasPalette(seed?: string | null): string[] {
  const random = createSeededRandom(seed == null ? '' : String(seed), RANDOM_MADRAS_RNG_NAMESPACE);
  const baseColors = MADRAS_SWATCH.filter((c) => !MADRAS_WHITES.includes(c));
  const n = 3 + Math.floor(random() * 3);
  const picked: string[] = [];
  while (picked.length < n) {
    const c = baseColors[Math.floor(random() * baseColors.length)];
    if (!picked.includes(c)) picked.push(c);
  }
  picked.push(MADRAS_WHITES[Math.floor(random() * MADRAS_WHITES.length)]);
  return picked;
}